import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth, API } from '../App';
import { Zap, Eye, EyeOff, Lock, Check, X as XIcon } from 'lucide-react';

// #544: Password requirements with real-time validation
function PasswordChecklist({ password, name, email }) {
  const checks = useMemo(() => [
    { label: '12+ characters', pass: password.length >= 12 },
    { label: 'Uppercase letter', pass: /[A-Z]/.test(password) },
    { label: 'Lowercase letter', pass: /[a-z]/.test(password) },
    { label: 'Number', pass: /[0-9]/.test(password) },
    { label: 'Special character', pass: /[^A-Za-z0-9]/.test(password) },
    { label: 'Not your name or email', pass: password.length > 0 && (
      !(name && password.toLowerCase().includes(name.toLowerCase())) &&
      !(email && password.toLowerCase().includes(email.split('@')[0].toLowerCase()))
    )},
  ], [password, name, email]);

  const allPass = checks.every(c => c.pass);

  return { checks, allPass, ChecklistUI: (
    <div className="space-y-1 mt-2">
      {checks.map((c, i) => (
        <div key={i} className="flex items-center gap-1.5 text-[11px] font-mono">
          {password.length === 0 ? (
            <span className="w-3 h-3 rounded-full border border-[#2A2A2A]" />
          ) : c.pass ? (
            <Check className="w-3 h-3 text-[#22C55E]" />
          ) : (
            <XIcon className="w-3 h-3 text-[#EF4444]" />
          )}
          <span className={password.length === 0 ? 'text-[#666]' : c.pass ? 'text-[#22C55E]' : 'text-[#EF4444]'}>
            {c.label}
          </span>
        </div>
      ))}
    </div>
  )};
}

export default function Signup() {
  const { signup } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteCode = searchParams.get('invite') || '';
  const workspaceInviteToken = searchParams.get('workspace_invite') || '';

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [showConfirmPw, setShowConfirmPw] = useState(false);
  const [invite, setInvite] = useState(inviteCode);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailLocked, setEmailLocked] = useState(false);
  const [wsName, setWsName] = useState('');
  const [passwordMethod, setPasswordMethod] = useState('user_set'); // from invite metadata

  const isWorkspaceInvite = !!workspaceInviteToken;

  // #539: Resolve workspace invite token
  useEffect(() => {
    if (!workspaceInviteToken) return;
    API.get(`/api/auth/workspace-invite/${workspaceInviteToken}`)
      .then(r => {
        setEmail(r.data.email);
        setEmailLocked(true);
        setWsName(r.data.workspace_name || '');
        if (r.data.name) setName(r.data.name);
        if (r.data.password_method) setPasswordMethod(r.data.password_method);
      })
      .catch(() => setError('Invalid or expired invite link'));
  }, [workspaceInviteToken]);

  const { checks, allPass, ChecklistUI } = PasswordChecklist({ password, name, email });
  const passwordsMatch = confirmPassword.length > 0 && password === confirmPassword;
  const canSubmit = isWorkspaceInvite
    ? (email && password && allPass && passwordsMatch && passwordMethod === 'user_set')
      || (email && passwordMethod !== 'user_set') // admin-set or auto-generated — no pw fields needed
    : (email && password);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // #544: Enforce password requirements on workspace invites
    if (isWorkspaceInvite && passwordMethod === 'user_set') {
      if (!allPass) { setError('Password does not meet requirements'); return; }
      if (!passwordsMatch) { setError('Passwords do not match'); return; }
    }

    setLoading(true);
    try {
      await signup(email, password, name, invite || undefined, workspaceInviteToken || undefined);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Signup failed');
    } finally {
      setLoading(false);
    }
  };

  // ── Workspace invite: simplified accept flow ──
  if (isWorkspaceInvite) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-6">
        <div className="w-full max-w-md">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 mb-8 justify-center">
            <div className="w-8 h-8 bg-[#FF4D00] flex items-center justify-center" style={{ clipPath: 'polygon(50% 0%, 100% 38%, 82% 100%, 18% 100%, 0% 38%)' }}>
              <Zap className="w-5 h-5 text-black" />
            </div>
            <span className="text-xl font-semibold font-['Barlow_Condensed'] text-[#F5F1E8]">Fray</span>
          </Link>

          {/* Welcome card */}
          <div className="bg-[#1A1A1A] border border-[#2A2A2A] rounded-xl p-8">
            <h1 className="text-2xl font-bold font-['Barlow_Condensed'] text-[#F5F1E8] text-center uppercase tracking-wide mb-1">
              {wsName ? `Welcome to ${wsName}!` : 'Welcome!'}
            </h1>
            <p className="text-sm text-[#A0A0A0] text-center mb-6">Set up your account to get started</p>

            {error && (
              <div className="p-3 mb-4 bg-[#A61E17]/10 border border-[#A61E17]/30 rounded-lg text-[#EF4444] text-sm font-mono">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Email — locked */}
              <div>
                <label className="text-xs text-[#A0A0A0] font-mono mb-1.5 block flex items-center gap-1">
                  Email <Lock className="w-3 h-3 text-[#F2A900]" />
                </label>
                <input
                  type="email" value={email} readOnly
                  className="w-full px-4 py-3 bg-[#0F0F0F] border border-[#2A2A2A] rounded-lg text-[#F5F5F5] text-sm font-mono opacity-70 cursor-not-allowed"
                />
              </div>

              {/* Name — editable */}
              <div>
                <label className="text-xs text-[#A0A0A0] font-mono mb-1.5 block">Your Name</label>
                <input
                  type="text" value={name} onChange={e => setName(e.target.value)}
                  className="w-full px-4 py-3 bg-[#0F0F0F] border border-[#2A2A2A] rounded-lg text-[#F5F5F5] text-sm focus:outline-none focus:border-[#FF4D00] transition-colors"
                  placeholder="Your name"
                />
              </div>

              {/* Password — only if user_set method */}
              {passwordMethod === 'user_set' && (
                <>
                  <div>
                    <label className="text-xs text-[#A0A0A0] font-mono mb-1.5 block">Create Password</label>
                    <div className="relative">
                      <input
                        type={showPw ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)}
                        className="w-full px-4 py-3 bg-[#0F0F0F] border border-[#2A2A2A] rounded-lg text-[#F5F5F5] text-sm focus:outline-none focus:border-[#FF4D00] transition-colors pr-12"
                        placeholder="Create a strong password" required
                      />
                      <button type="button" onClick={() => setShowPw(!showPw)}
                        className="absolute right-1 top-1/2 -translate-y-1/2 text-[#A0A0A0] p-2 min-w-[44px] min-h-[44px] flex items-center justify-center">
                        {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    {ChecklistUI}
                  </div>

                  <div>
                    <label className="text-xs text-[#A0A0A0] font-mono mb-1.5 block">Confirm Password</label>
                    <div className="relative">
                      <input
                        type={showConfirmPw ? 'text' : 'password'} value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
                        className={`w-full px-4 py-3 bg-[#0F0F0F] border rounded-lg text-[#F5F5F5] text-sm focus:outline-none transition-colors pr-12 ${
                          confirmPassword.length === 0 ? 'border-[#2A2A2A]' : passwordsMatch ? 'border-[#22C55E]' : 'border-[#EF4444]'
                        }`}
                        placeholder="Confirm your password" required
                      />
                      <button type="button" onClick={() => setShowConfirmPw(!showConfirmPw)}
                        className="absolute right-1 top-1/2 -translate-y-1/2 text-[#A0A0A0] p-2 min-w-[44px] min-h-[44px] flex items-center justify-center">
                        {showConfirmPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    {confirmPassword.length > 0 && !passwordsMatch && (
                      <p className="text-[11px] text-[#EF4444] font-mono mt-1">Passwords do not match</p>
                    )}
                  </div>
                </>
              )}

              {/* Submit */}
              <button
                type="submit"
                disabled={loading || !canSubmit}
                className="w-full py-3.5 min-h-[48px] bg-[#FF4D00] text-[#0A0A0A] font-bold text-sm uppercase tracking-wide rounded-lg hover:bg-[#E63E00] transition-colors disabled:opacity-50 mt-2"
              >
                {loading ? 'Setting up...' : wsName ? `Join ${wsName}` : 'Join Workspace'}
              </button>
            </form>

            <p className="text-center text-xs text-[#666] mt-4">
              Already have an account?{' '}
              <Link to="/login" className="text-[#FF4D00] hover:underline">Log in</Link>
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ── Standard signup flow (unchanged) ──
  return (
    <div className="min-h-screen bg-[#0F0F0F] flex items-center justify-center px-6">
      <div className="w-full max-w-sm">
        <Link to="/" className="flex items-center gap-2 mb-10 justify-center" data-testid="signup-logo">
          <div className="w-8 h-8 bg-[#FF4D00] flex items-center justify-center" style={{ clipPath: 'polygon(50% 0%, 100% 38%, 82% 100%, 18% 100%, 0% 38%)' }}>
            <Zap className="w-5 h-5 text-black" />
          </div>
          <span className="text-xl font-semibold font-['Barlow_Condensed']">Fray</span>
        </Link>

        <h1 className="text-2xl font-bold mb-2 font-['Barlow_Condensed'] text-center" data-testid="signup-heading">Create your account</h1>
        <p className="text-[#A0A0A0] text-sm text-center mb-8">
          {invite ? 'You have been invited to Fray' : 'Start running AI committee debates'}
        </p>

        {error && <div className="p-3 mb-4 bg-[#EF4444]/10 border border-[#EF4444]/30 text-[#EF4444] text-sm" data-testid="signup-error">{error}</div>}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-xs text-[#A0A0A0] font-mono mb-1 block">Name</label>
            <input
              type="text" value={name} onChange={e => setName(e.target.value)}
              className="w-full px-4 py-3 bg-[#1A1A1A] border border-[#2A2A2A] text-[#F5F5F5] text-sm focus:outline-none focus:border-[#FF4D00] transition-colors"
              placeholder="Your name" data-testid="signup-name"
            />
          </div>
          <div>
            <label className="text-xs text-[#A0A0A0] font-mono mb-1 block">Email</label>
            <input
              type="email" value={email} onChange={e => setEmail(e.target.value)}
              className="w-full px-4 py-3 bg-[#1A1A1A] border border-[#2A2A2A] text-[#F5F5F5] text-sm focus:outline-none focus:border-[#FF4D00] transition-colors"
              placeholder="you@company.com" required data-testid="signup-email"
            />
          </div>
          <div>
            <label className="text-xs text-[#A0A0A0] font-mono mb-1 block">Password</label>
            <div className="relative">
              <input
                type={showPw ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)}
                className="w-full px-4 py-3 bg-[#1A1A1A] border border-[#2A2A2A] text-[#F5F5F5] text-sm focus:outline-none focus:border-[#FF4D00] transition-colors pr-10"
                placeholder="Min 6 characters" required minLength={6} data-testid="signup-password"
              />
              <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-1 top-1/2 -translate-y-1/2 text-[#A0A0A0] p-2 min-w-[44px] min-h-[44px] flex items-center justify-center">
                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          {(invite || inviteCode) && (
            <div>
              <label className="text-xs text-[#A0A0A0] font-mono mb-1 block">Invite Code</label>
              <input
                type="text" value={invite} onChange={e => setInvite(e.target.value)}
                className="w-full px-4 py-3 bg-[#1A1A1A] border border-[#2A2A2A] text-[#F5F5F5] text-sm focus:outline-none focus:border-[#FF4D00] transition-colors font-mono"
                placeholder="FRAY-XXXXXXXX" data-testid="signup-invite"
              />
            </div>
          )}
          <button
            type="submit" disabled={loading}
            className="w-full py-3 min-h-[44px] bg-[#FF4D00] text-black font-semibold text-sm hover:bg-[#FF4D00]/90 transition-colors disabled:opacity-50"
            data-testid="signup-submit"
          >
            {loading ? 'Creating account...' : 'Sign up'}
          </button>
        </form>

        <SocialSignupButtons />

        <p className="text-center text-sm text-[#A0A0A0] mt-6 min-h-[44px] flex items-center justify-center">
          Already have an account?&nbsp;<Link to="/login" className="text-[#FF4D00] hover:underline" data-testid="login-link">Log in</Link>
        </p>
        {!invite && !inviteCode && (
          <p className="text-center text-xs text-[#A0A0A0] mt-2 min-h-[44px] flex items-center justify-center">
            Have an invite code?&nbsp;<button onClick={() => setInvite(' ')} className="text-[#FF4D00] hover:underline">Enter it here</button>
          </p>
        )}
      </div>
    </div>
  );
}

function SocialSignupButtons() {
  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="mt-4">
      <div className="flex items-center gap-3 mb-4">
        <div className="flex-1 h-px bg-[#2A2A2A]" />
        <span className="text-[10px] text-[#A0A0A0] font-mono">OR SIGN UP WITH</span>
        <div className="flex-1 h-px bg-[#2A2A2A]" />
      </div>
      <div className="flex gap-3">
        <button onClick={handleGoogle}
          className="flex-1 py-3 min-h-[44px] border border-[#2A2A2A] text-sm text-[#F5F5F5] hover:border-[#A0A0A0] transition-colors flex items-center justify-center gap-2"
          data-testid="google-signup-btn">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A10.96 10.96 0 001 12c0 1.77.42 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
          Google
        </button>
        <button onClick={() => {}}
          className="flex-1 py-3 min-h-[44px] border border-[#2A2A2A] text-sm text-[#F5F5F5] hover:border-[#A0A0A0] transition-colors flex items-center justify-center gap-2"
          data-testid="github-signup-btn">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z"/></svg>
          GitHub
        </button>
      </div>
    </div>
  );
}
